import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalProfile } from "../local/useLocalProfile";
import { newRoomCode } from "../multiplayer/types";
import { getBattlePlayerId } from "../multiplayer/battleIdentity";
import { WORD_PACKS, getWordPack } from "../data/word-packs";
import { listCustomPacks } from "../local/customPacks";
import { AvatarPreview } from "../components/AvatarEditor";
import { BattlePodiumScene } from "../components/BattlePodiumScene";
import { GameImage } from "../game/ui/GameImage";
import { startPodiumMusic, fadeOutPodiumMusic } from "../game/audio";
import { useDrawRoom } from "../drawit/useDrawRoom";
import { DrawCanvas, DRAW_COLORS, BRUSH_SIZES, type DrawCanvasApi } from "../drawit/DrawCanvas";
import { wordChoiceImage } from "../drawit/wordImages";
import type { DrawJoinOpts, DrawPlayer } from "../drawit/types";

const TIME_OPTIONS = [0, 45, 75, 100];
const ROUND_OPTIONS = [4, 8, 12, 16];

/** DrawIt Host Display — teacher's big screen. Round 1 the teacher draws
 *  (demo, so the host device itself becomes the interactive canvas);
 *  presenter/guide from round 2 on. */
export function DrawHost({ codeOverride, embedded }: { codeOverride?: string; embedded?: boolean } = {}) {
  const nav = useNavigate();
  const { profile } = useLocalProfile();
  const code = useMemo(() => codeOverride ?? newRoomCode(), [codeOverride]);
  const allPacks = useMemo(() => [...WORD_PACKS, ...listCustomPacks()], []);

  const myId = profile ? getBattlePlayerId(profile.id, code, "host") : "";
  const joinOpts = useMemo<DrawJoinOpts | null>(
    () => (profile ? { code, role: "host", me: { id: myId, displayName: profile.displayName, avatarConfig: profile.avatarConfig } } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile, code],
  );
  const { client, state, error } = useDrawRoom(joinOpts);
  const [color, setColor] = useState(DRAW_COLORS[0]!);
  const [brushWidth, setBrushWidth] = useState<number>(BRUSH_SIZES[1]);
  const canvasApiRef = useRef<DrawCanvasApi | null>(null);

  // Podium music.
  useEffect(() => {
    if (state?.phase === "podium") { startPodiumMusic(); return () => fadeOutPodiumMusic(1200); }
  }, [state?.phase]);

  useEffect(() => { if (!embedded && !profile) nav("/", { replace: true }); }, [embedded, profile, nav]);
  if (!embedded && !profile) return null;

  const settings = state?.settings ?? { packIds: [], roundTimeSec: 75, roundCount: 8, nextDrawerMode: "random" as const };
  const togglePack = (id: string) => {
    const has = settings.packIds.includes(id);
    client?.setSettings({ packIds: has ? settings.packIds.filter((p) => p !== id) : [...settings.packIds, id] });
  };
  const poolPreview = useMemo(() => {
    const seen = new Set<string>();
    for (const id of settings.packIds) for (const w of getWordPack(id)?.words ?? []) seen.add(w.english);
    return seen.size;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.packIds.join(",")]);
  const canStart = state != null && state.players.some((p) => p.id !== myId) && settings.packIds.length > 0 && poolPreview >= 3;
  const startGame = () => {
    const seen = new Set<string>();
    const pool: string[] = [];
    for (const id of settings.packIds) {
      for (const w of getWordPack(id)?.words ?? []) { if (!seen.has(w.english)) { seen.add(w.english); pool.push(w.english); } }
    }
    if (pool.length) client?.start(pool);
  };

  if (error) {
    return (
      <div className="quizzle-host">
        <p className="quizzle-error">⚠️ {error}</p>
        {!embedded && <button className="btn btn-ghost" onClick={() => nav("/drawit")}>← Back</button>}
      </div>
    );
  }

  const ranked = [...(state?.players ?? [])].sort((a, b) => b.score - a.score);
  const isMyTurn = !!state && state.drawerId === myId;
  const drawer = state?.players.find((p) => p.id === state.drawerId);
  const guessers = state?.players.filter((p) => p.id !== state.drawerId) ?? [];
  const guessedCount = guessers.filter((p) => p.guessedCorrect).length;

  // ── CHOOSING ──────────────────────────────────────────────────────────
  if (state?.phase === "choosing") {
    if (isMyTurn) {
      return (
        <div className="drawit-stage">
          <h1 className="drawit-choose-h">Pick a word to draw</h1>
          <div className="drawit-word-row">
            {(state.wordOptions ?? []).map((w) => (
              <button key={w} className="drawit-word-btn" onClick={() => client?.chooseWord(state.wordOptions!.indexOf(w))}>
                <GameImage className="drawit-word-img" src={wordChoiceImage(w)} alt="" fallbackLabel={w[0]?.toUpperCase() ?? "?"} />
                <span>{w}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="drawit-stage">
        <h1 className="drawit-choose-h">{drawer?.displayName ?? "Someone"} is picking a word…</h1>
        <p className="quizzle-dim">Round {state.roundIndex + 1} / {settings.roundCount}</p>
      </div>
    );
  }

  // ── DRAWING ──────────────────────────────────────────────────────────
  if (state?.phase === "drawing") {
    return (
      <DrawingPresenter
        state={state}
        client={client}
        isMyTurn={isMyTurn}
        timed={settings.roundTimeSec > 0}
        color={color}
        setColor={setColor}
        brushWidth={brushWidth}
        setBrushWidth={setBrushWidth}
        canvasApiRef={canvasApiRef}
        guessedCount={guessedCount}
        guesserCount={guessers.length}
        drawer={drawer}
        settings={settings}
      />
    );
  }

  // ── REVEAL ───────────────────────────────────────────────────────────
  if (state?.phase === "reveal") {
    return (
      <div className="drawit-stage">
        <h2 className="quizzle-reveal-h">The word was…</h2>
        <div className="drawit-reveal-row">
          <GameImage className="drawit-reveal-img" src={wordChoiceImage(state.revealedWord ?? "")} alt="" fallbackLabel="?" />
          <h1 className="drawit-revealed-word">{state.revealedWord}</h1>
        </div>
        <div className="quizzle-leaderboard">
          {ranked.map((p, i) => (
            <div key={p.id} className={`quizzle-lb-row quizzle-pop ${i < 3 ? `top-${i + 1}` : ""}`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className="quizzle-lb-rank">{i + 1}</span>
              <span className="quizzle-lb-avatar"><AvatarPreview cfg={p.avatarConfig} /></span>
              <span className="quizzle-lb-name">{p.displayName}</span>
              {p.lastDelta > 0 && <span className="quizzle-lb-delta">+{p.lastDelta}</span>}
              <span className="quizzle-lb-score">{p.score}</span>
            </div>
          ))}
        </div>
        <DrawTurnTracker players={state.players} myId={myId} />
        <NextRoundControls state={state} client={client} myId={myId} />
      </div>
    );
  }

  // ── PODIUM ───────────────────────────────────────────────────────────
  if (state?.phase === "podium") {
    const top3 = ranked.slice(0, 3);
    return (
      <div className="quizzle-stage quizzle-stage-podium">
        <h1 className="quizzle-podium-title">🏆 Final Results</h1>
        {top3.length > 0 && (
          <div className="quizzle-podium-scene">
            <BattlePodiumScene players={top3.map((p) => ({ id: p.id, displayName: p.displayName, avatarConfig: p.avatarConfig }))} />
          </div>
        )}
        <div className="quizzle-podium-scores">
          {ranked.slice(0, 8).map((p, i) => (
            <div key={p.id} className="quizzle-lb-row">
              <span className="quizzle-lb-rank">{i + 1}</span>
              <span className="quizzle-lb-avatar"><AvatarPreview cfg={p.avatarConfig} /></span>
              <span className="quizzle-lb-name">{p.displayName}</span>
              <span className="quizzle-lb-score">{p.score}</span>
            </div>
          ))}
        </div>
        <div className="quizzle-stage-actions">
          <button className="btn btn-green btn-big" onClick={() => client?.backToLobby()}>🔁 Back to Lobby</button>
          {!embedded && <button className="btn btn-ghost" onClick={() => nav("/drawit")}>End game</button>}
        </div>
      </div>
    );
  }

  // ── LOBBY ────────────────────────────────────────────────────────────
  return (
    <div className="quizzle-host">
      <header className="quizzle-host-top">
        {embedded ? <span /> : <button className="btn btn-ghost" onClick={() => nav("/drawit")}>← Back</button>}
        <h1 className="quizzle-host-title">🎨 DrawIt</h1>
        <div className="quizzle-code-card">
          <span className="quizzle-code-label">Join code</span>
          <span className="quizzle-code-value">{code}</span>
        </div>
      </header>

      <div className="quizzle-host-grid">
        <section className="quizzle-panel">
          <h2>Settings</h2>

          <div className="quizzle-setting">
            <label>Word packs <span className="quizzle-dim">(pick one or more)</span></label>
            <div className="quizzle-pack-grid">
              {allPacks.map((p) => (
                <button key={p.id} className={`quizzle-pack ${settings.packIds.includes(p.id) ? "is-on" : ""}`} onClick={() => togglePack(p.id)}>
                  <strong>{p.title}</strong><small>{p.words.length} words</small>
                </button>
              ))}
            </div>
          </div>

          <div className="quizzle-setting">
            <label>Time per round</label>
            <div className="quizzle-pill-row">
              {TIME_OPTIONS.map((t) => (
                <button key={t} className={`quizzle-pill ${settings.roundTimeSec === t ? "is-on" : ""}`} onClick={() => client?.setSettings({ roundTimeSec: t })}>
                  {t === 0 ? "Off (teacher)" : `${t}s`}
                </button>
              ))}
            </div>
          </div>

          <div className="quizzle-setting">
            <label>Rounds</label>
            <div className="quizzle-pill-row">
              {ROUND_OPTIONS.map((n) => (
                <button key={n} className={`quizzle-pill ${settings.roundCount === n ? "is-on" : ""}`} onClick={() => client?.setSettings({ roundCount: n })}>{n}</button>
              ))}
            </div>
          </div>

          <div className="quizzle-setting">
            <label>Next drawer (after round 1)</label>
            <div className="quizzle-pill-row">
              <button className={`quizzle-pill ${settings.nextDrawerMode === "random" ? "is-on" : ""}`} onClick={() => client?.setSettings({ nextDrawerMode: "random" })}>🎲 Random</button>
              <button className={`quizzle-pill ${settings.nextDrawerMode === "host-picks" ? "is-on" : ""}`} onClick={() => client?.setSettings({ nextDrawerMode: "host-picks" })}>👉 I pick</button>
            </div>
          </div>
        </section>

        <section className="quizzle-panel">
          <h2>Players <span className="quizzle-dim">({state?.players.length ?? 0})</span></h2>
          <p className="quizzle-dim quizzle-join-hint">Students open DrawIt → Join → enter <strong>{code}</strong></p>
          <div className="quizzle-players">
            {(state?.players ?? []).filter((p) => p.id !== myId).map((p) => (
              <div key={p.id} className="quizzle-player">
                <span className="quizzle-player-avatar"><AvatarPreview cfg={p.avatarConfig} /></span>
                <strong>{p.displayName}</strong>
              </div>
            ))}
            {(state?.players.length ?? 0) <= 1 && <p className="quizzle-dim">Waiting for players to join…</p>}
          </div>
          <p className="quizzle-dim">You'll draw first (round 1 demo) — students take turns from round 2.</p>
        </section>
      </div>

      <footer className="quizzle-host-actions">
        <button className="btn btn-green btn-big" disabled={!canStart} onClick={startGame}>▶ Start DrawIt</button>
        {!canStart && (
          <p className="quizzle-dim">
            {settings.packIds.length > 0 && poolPreview < 3
              ? "Pick at least 3 words' worth of packs — DrawIt offers 3 word choices each round."
              : "Pick at least one pack and wait for a player to join."}
          </p>
        )}
      </footer>
    </div>
  );
}

/** Host's view during the drawing phase — either the interactive canvas
 *  (round 1, host is drawing) or a read-only mirror + round controls. */
function DrawingPresenter({ state, client, isMyTurn, timed, color, setColor, brushWidth, setBrushWidth, canvasApiRef, guessedCount, guesserCount, drawer, settings }: {
  state: NonNullable<ReturnType<typeof useDrawRoom>["state"]>;
  client: ReturnType<typeof useDrawRoom>["client"];
  isMyTurn: boolean;
  timed: boolean;
  color: string;
  setColor: (c: string) => void;
  brushWidth: number;
  setBrushWidth: (w: number) => void;
  canvasApiRef: React.MutableRefObject<DrawCanvasApi | null>;
  guessedCount: number;
  guesserCount: number;
  drawer?: { displayName: string };
  settings: { roundTimeSec: number; roundCount: number };
}) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!timed) return;
    const id = window.setInterval(() => force((n) => (n + 1) & 0xffff), 250);
    return () => window.clearInterval(id);
  }, [timed]);
  const secsLeft = timed && state.roundStartedAt ? Math.max(0, Math.ceil(settings.roundTimeSec - (Date.now() - state.roundStartedAt) / 1000)) : 0;
  const blanks = state.wordLength ? Array.from({ length: state.wordLength }, () => "_").join(" ") : "";

  return (
    <div className="drawit-stage">
      <div className="drawit-top">
        <span>Round {state.roundIndex + 1} / {settings.roundCount}</span>
        <span className="drawit-blanks">{isMyTurn ? "You're drawing!" : `${drawer?.displayName ?? "Someone"} is drawing`} {blanks && `· ${blanks}`}</span>
        {timed ? <span className={`quizzle-timer ${secsLeft <= 10 ? "is-urgent" : ""}`}>{secsLeft}s</span> : <span className="quizzle-timer quizzle-timer-off">⏳ No timer</span>}
      </div>
      <div className="drawit-canvas-wrap">
        <DrawCanvas client={client} interactive={isMyTurn} initialStrokes={state.strokes} resetKey={state.roundIndex} color={color} width={brushWidth} apiRef={canvasApiRef} className="drawit-canvas-big" />
      </div>
      {isMyTurn && (
        <div className="drawit-toolbar">
          {DRAW_COLORS.map((c) => (
            <button key={c} className={`drawit-swatch ${color === c ? "is-on" : ""}`} style={{ background: c }} onClick={() => setColor(c)} aria-label={`Color ${c}`} />
          ))}
          <span className="drawit-toolbar-sep" aria-hidden />
          {BRUSH_SIZES.map((w) => (
            <button key={w} className={`drawit-sizebtn ${brushWidth === w ? "is-on" : ""}`} onClick={() => setBrushWidth(w)} aria-label={`Brush size ${w}`}>
              <span className="drawit-sizebtn-dot" style={{ width: 6 + w, height: 6 + w }} />
            </button>
          ))}
          <button className="btn btn-ghost" onClick={() => canvasApiRef.current?.undo()}>↩ Undo</button>
          <button className="btn btn-ghost" onClick={() => canvasApiRef.current?.clear()}>🗑 Clear</button>
        </div>
      )}
      <div className="drawit-status-row">
        <span className="quizzle-dim">{guessedCount} / {guesserCount} guessed correctly</span>
        <button className="btn btn-ghost" onClick={() => client?.endRoundNow()}>Reveal now ▶</button>
      </div>
    </div>
  );
}

function NextRoundControls({ state, client, myId }: { state: NonNullable<ReturnType<typeof useDrawRoom>["state"]>; client: ReturnType<typeof useDrawRoom>["client"]; myId: string }) {
  const [pickOpen, setPickOpen] = useState(false);
  const eligible = state.players.filter((p) => p.online && p.id !== myId);
  // Students who haven't had a turn yet float to the top so the teacher can
  // easily give everyone a go before repeating anyone.
  const sorted = [...eligible].sort((a, b) => Number(a.hasDrawn) - Number(b.hasDrawn));
  if (state.settings.nextDrawerMode === "host-picks") {
    if (pickOpen) {
      return (
        <div className="drawit-pick-panel">
          <p className="quizzle-dim">Who draws next?</p>
          <div className="quizzle-pill-row">
            {sorted.map((p) => (
              <button key={p.id} className="quizzle-pill" onClick={() => { client?.nextRound(p.id); setPickOpen(false); }}>
                {!p.hasDrawn && <span aria-hidden>🆕 </span>}{p.displayName}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost" onClick={() => setPickOpen(false)}>Cancel</button>
        </div>
      );
    }
    return (
      <div className="quizzle-stage-actions">
        <button className="btn btn-green" onClick={() => setPickOpen(true)} disabled={eligible.length === 0}>Next Round ▶</button>
      </div>
    );
  }
  return (
    <div className="quizzle-stage-actions">
      <button className="btn btn-green" onClick={() => client?.nextRound()} disabled={eligible.length === 0}>Next Round ▶</button>
    </div>
  );
}

/** "Who's up next" strip — shows every student with a checkmark once
 *  they've had a turn drawing, so the class can see who's still waiting. */
function DrawTurnTracker({ players, myId }: { players: DrawPlayer[]; myId: string }) {
  const students = players.filter((p) => p.id !== myId);
  if (students.length === 0) return null;
  return (
    <div className="drawit-turn-tracker">
      <span className="quizzle-dim">Turn order</span>
      <div className="drawit-turn-row">
        {students.map((p) => (
          <span key={p.id} className={`drawit-turn-chip ${p.hasDrawn ? "has-drawn" : ""}`}>
            <span className="drawit-turn-avatar"><AvatarPreview cfg={p.avatarConfig} /></span>
            {p.displayName}
            {p.hasDrawn && <span aria-hidden> ✓</span>}
          </span>
        ))}
      </div>
    </div>
  );
}
